/**
 * ConceptSelector — 프롬프트 블록 4종(스타일/포즈/의상/카메라) 선택 UI
 *
 * imageUrl이 있는 블록은 이미지 카드로, 없으면 칩으로 표시
 */

import { useMemo } from 'react'
import { cn } from '@/lib/cn'
import type { PromptBlockSummary } from '@/hooks/usePromptBlocks'
import type { PromptBlockType, PromptBlockSelection } from '@cugumi/shared/schemas'
import styles from './ConceptSelector.module.css'

export type { PromptBlockSelection } from '@cugumi/shared/schemas'

const BLOCK_LABELS: Record<PromptBlockType, string> = {
  STYLE: '스타일',
  POSE: '포즈',
  WARDROBE: '의상',
  CAMERA: '카메라',
}

interface ConceptSelectorProps {
  grouped: Record<PromptBlockType, PromptBlockSummary[]>
  selection: Partial<PromptBlockSelection>
  onChange: (selection: Partial<PromptBlockSelection>) => void
  isLoading?: boolean
}

const SLUG_KEYS: Record<PromptBlockType, keyof PromptBlockSelection> = {
  STYLE: 'styleSlug',
  POSE: 'poseSlug',
  WARDROBE: 'wardrobeSlug',
  CAMERA: 'cameraSlug',
}

const BLOCK_ORDER: PromptBlockType[] = ['STYLE', 'POSE', 'WARDROBE', 'CAMERA']

export function ConceptSelector({ grouped, selection, onChange, isLoading }: ConceptSelectorProps) {
  // 섹션별 이미지 유무를 미리 계산 (렌더마다 .some() 반복 방지)
  const sectionHasImages = useMemo(() => {
    const result = {} as Record<PromptBlockType, boolean>
    for (const type of BLOCK_ORDER) {
      result[type] = grouped[type].some((b) => b.imageUrl)
    }
    return result
  }, [grouped])

  if (isLoading) {
    return <div className={styles.section}>블록 로딩 중...</div>
  }

  return (
    <div>
      {BLOCK_ORDER.map((type) => {
        const blocks = grouped[type]
        if (blocks.length === 0) return null

        const slugKey = SLUG_KEYS[type]
        const selectedSlug = selection[slugKey]
        const hasImages = sectionHasImages[type]

        return (
          <div key={type} className={styles.section}>
            <span className={styles.sectionLabel}>{BLOCK_LABELS[type]}</span>
            <div className={hasImages ? styles.cardGrid : styles.chipGrid}>
              {blocks.map((block) => {
                const isSelected = selectedSlug === block.slug

                if (block.imageUrl) {
                  return (
                    <button
                      key={block.id}
                      type="button"
                      className={cn(styles.card, isSelected && styles.cardSelected)}
                      onClick={() => onChange({ ...selection, [slugKey]: block.slug })}
                    >
                      <div className={styles.cardImageWrap}>
                        <img src={block.imageUrl} alt={block.nameKo} className={styles.cardImage} />
                        {isSelected && (
                          <span className={styles.cardCheck}>✓</span>
                        )}
                      </div>
                      <span className={styles.cardLabel}>{block.nameKo}</span>
                    </button>
                  )
                }

                return (
                  <button
                    key={block.id}
                    type="button"
                    className={cn(styles.chip, isSelected && styles.chipSelected)}
                    onClick={() => onChange({ ...selection, [slugKey]: block.slug })}
                  >
                    {isSelected && <span className={styles.chipCheck}>✓</span>}
                    {block.nameKo}
                  </button>
                )
              })}
            </div>
          </div>
        )
      })}
    </div>
  )
}
